import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import AOS from "aos";
import "aos/dist/aos.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axiosInstance from "../../axiosInstance";
import { Link, useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import Header from "./Header";

import AdminDashboardLayout from "./AdminDashboardLayout";
import * as bootstrap from "bootstrap";

const UsersManagementPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const direction = i18n.language === "ar" ? "rtl" : "ltr";
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [homepageFilters, setHomepageFilters] = useState([]);
  // الحالة للفلاتر
  const [filters, setFilters] = useState({
    role: "",
    status: "",
    homepage: "",
    keyword: "",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(12);
  const [totalPages, setTotalPages] = useState(1);

  // جلب البيانات
  const fetchUsers = async (page = 1) => {
    try {
      const params = new URLSearchParams();

      // الفلاتر
      if (filters.status !== "") params.append("status", filters.status);
      if (filters.role !== "") params.append("role", filters.role);
      if (filters.homepage !== "") params.append("homepage", filters.homepage);
      if (filters.keyword.trim() !== "")
        params.append("keyword", filters.keyword);

      params.append("page", page);
      params.append("pageSize", pageSize);

      console.log("Fetching page:", page);
      console.log("Params:", params.toString());

      const res = await axiosInstance.get("/api/v1/admins/users", { params });

      const { items, metadata } = res.data;

      console.log("API returned page:", metadata.pageNumber);

      setUsers(items);
      setTotalPages(metadata.totalPages);
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };

  // تغيير الصفحة
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      console.log("Going to page:", page);
      setCurrentPage(page);
    }
  };

  useEffect(() => {
    fetchUsers(currentPage);
  }, [currentPage]);

  // جلب lookups للفلاتر
  const fetchLookups = async () => {
    try {
      const [rolesRes, statusRes, homepageRes] = await Promise.all([
        axiosInstance.get("/api/v1/lookups/roles"),
        axiosInstance.get("/api/v1/lookups/status-filter"),
        axiosInstance.get("/api/v1/lookups/home-page-filter"),
      ]);

      setRoles(rolesRes.data?.items || []);
      setStatuses(statusRes.data?.items || []);
      setHomepageFilters(homepageRes.data?.items || []);
    } catch (err) {
      console.error("Error fetching lookup data:", err);
    }
  };

  // حذف مستخدم
  const deleteUser = async (userId) => {
    if (!window.confirm(t("confirmDeleteUser"))) return;
    try {
      await axiosInstance.delete(`/api/v1/admins/users/${userId}`);
      fetchUsers();
    } catch (err) {
      console.error("Error deleting user:", err);
    }
  };

  // تبديل المميز أو النخبة (type: 2 للمميز، 3 للنخبة)
  const toggleHomeStatus = async (userId, type) => {
    try {
      await axiosInstance.put(`/api/v1/admins/users/${userId}/home`, null, {
        params: { type },
      });
      fetchUsers();
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.title ||
        error.message ||
        "لقد وصلت إلى الحد الأقصى لعدد المتحدثين المميزين المسموح به";
      toast.error(errorMessage);
      console.error(t("limitReachedOrError"), error);
    }
  };

  // تحديث المستخدمين عند تغيير الفلاتر
  const applyFilters = () => {
    fetchUsers();
  };

  // تنفيذ عند التحميل الأولي
  useEffect(() => {
    fetchLookups();
    fetchUsers();
  }, []);

  useEffect(() => {
    fetchUsers(1);
    setCurrentPage(1);
  }, [filters]);

  return (
    <AdminDashboardLayout pageTitle="إدارة المستخدمين" dir={direction}>
      <div>
        {/* العنوان ومسارات التصفح */}
        <div className="row mb-4">
          <div className="col-md-12">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h2 className="mb-1">{t("userManagement")}</h2>
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <a href="#">{t("dashboard")}</a>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      {t("sidebar.users")}
                    </li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>
        </div>

        {/* Filters Section */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <div className="row g-3">
                  {/* الدور */}
                  <div className="col-md-3">
                    <label
                      htmlFor="roleFilter"
                      className="form-label"
                      data-i18n="role"
                    >
                      {t("role")}
                    </label>
                    <select
                      id="roleFilter"
                      className="form-select"
                      value={filters.role}
                      onChange={(e) =>
                        setFilters((f) => ({ ...f, role: e.target.value }))
                      }
                    >
                      <option value="">{t("all")}</option>
                      {roles.map((role) => (
                        <option key={role.id} value={role.id}>
                          {role.name.trim() ? role.name : `Role #${role.id}`}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* الحالة */}
                  <div className="col-md-3">
                    <label
                      htmlFor="statusFilter"
                      className="form-label"
                      data-i18n="status"
                    >
                      {t("status")}
                    </label>
                    <select
                      id="statusFilter"
                      className="form-select"
                      value={filters.status}
                      onChange={(e) =>
                        setFilters((f) => ({ ...f, status: e.target.value }))
                      }
                    >
                      <option value="">{t("all")}</option>
                      {statuses.map((status) => (
                        <option key={status.id} value={status.id}>
                          {status.name.trim()
                            ? status.name
                            : `Status #${status.id}`}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* المميزون - النخبة */}
                  <div className="col-md-2">
                    <label htmlFor="featuredFilter" className="form-label">
                      {t("featuredOrTop")}
                    </label>
                    <select
                      id="featuredFilter"
                      className="form-select"
                      value={filters.homepage}
                      onChange={(e) =>
                        setFilters((f) => ({ ...f, homepage: e.target.value }))
                      }
                    >
                      <option value="">{t("all")}</option>
                      {homepageFilters.map((hp) => (
                        <option key={hp.id} value={hp.id}>
                          {hp.name.trim() ? hp.name : `Filter #${hp.id}`}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* بحث */}
                  <div className="col-md-4">
                    <label htmlFor="searchFilter" className="form-label">
                      {/* {t("searchs.user")} */}
                    </label>
                    <div className="input-group">
                      <input
                        id="searchFilter"
                        type="text"
                        className="form-control"
                        placeholder={t("searchs.user")}
                        value={filters.keyword}
                        onChange={(e) =>
                          setFilters((f) => ({ ...f, keyword: e.target.value }))
                        }
                      />
                      <button
                        className="btn btn-primary-custom h-100"
                        type="button"
                        onClick={applyFilters}
                      >
                        <i className="fas fa-search"></i>
                      </button>
                    </div>
                  </div>

                  {/* زر تطبيق الفلاتر */}
                  {/* <div className="col-md-2 d-flex align-items-end">
                    <button
                      className="btn btn-primary-custom w-100 h-100"
                      onClick={applyFilters}
                    >
                      <i className="fas fa-filter me-2"></i> {t("applyFilters")}
                    </button>
                  </div> */}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* جدول المستخدمين */}
        <table className="table align-middle">
          <thead>
            <tr>
              <th>{t("name")}</th>
              <th>{t("email")}</th>
              <th>{t("role")}</th>
              <th>{t("status")}</th>
              <th>{t("featured")}</th>
              <th>{t("top")}</th>
              <th>{t("joinDate")}</th>
              <th>{t("actions")}</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && (
              <tr>
                <td colSpan="8" className="text-center">
                  {t("noUsersFound")}
                </td>
              </tr>
            )}
            {users.map((user) => (
              <tr key={user.id}>
                <td>
                  {user.arabicFullName || user.englishFullName || user.userName}
                </td>
                <td>{user.email}</td>
                <td>{user.userRoleName}</td>
                <td>{user.userStatusName}</td>
                <td>
                  <input
                    type="checkbox"
                    checked={user.isFeatured}
                    onChange={() => toggleHomeStatus(user.id, 2)}
                  />
                </td>
                <td>
                  <input
                    type="checkbox"
                    checked={user.isTop}
                    onChange={() => toggleHomeStatus(user.id, 3)}
                  />
                </td>
                <td>{new Date(user.createdOn).toLocaleDateString()}</td>
                <td>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => deleteUser(user.id)}
                  >
                    {t("delete")}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <nav>
          <ul className="pagination justify-content-center mt-4">
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => goToPage(currentPage - 1)}
              >
                &laquo;
              </button>
            </li>

            {Array.from({ length: totalPages }, (_, i) => (
              <li
                key={i}
                className={`page-item ${currentPage === i + 1 ? "active" : ""}`}
              >
                <button className="page-link" onClick={() => goToPage(i + 1)}>
                  {i + 1}
                </button>
              </li>
            ))}

            <li
              className={`page-item ${
                currentPage === totalPages ? "disabled" : ""
              }`}
            >
              <button
                className="page-link"
                onClick={() => goToPage(currentPage + 1)}
              >
                &raquo;
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </AdminDashboardLayout>
  );
};

export default UsersManagementPage;
